import { Slide } from '@mui/material';
import PaymentDialog from 'components/Paywall/PaymentDialog';
import PaywallDialog from 'components/Paywall/PaywallDialog';
import UpdateCardDialog from 'components/Paywall/UpdateCardDialog';
import WelcomeDialog from 'components/Paywall/WelcomeDialog';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import moment from 'moment';
import qs from 'query-string';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import stripeService from 'services/stripe';
import userService from 'services/user';
import { Alert, Button, Loading, Snackbar, Stack, Text } from 'shell/components';
import { TG_FREE_PLAN_PRICE_ID } from 'utils/constants';
import { useViewport } from './viewport';

const hourGlassSvg = (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
            d='M8.51953 15.5C8.51953 13.567 10.0865 12 12.0195 12C13.9525 12 15.5195 13.567 15.5195 15.5V19H8.51953V15.5Z'
            fill='#D3CCFF'
        />
        <path
            d='M16.5995 8.12V5.44H18.3195C18.4075 5.44 18.4795 5.368 18.4795 5.28V4.16C18.4795 4.072 18.4075 4 18.3195 4H5.67953C5.59153 4 5.51953 4.072 5.51953 4.16V5.28C5.51953 5.368 5.59153 5.44 5.67953 5.44H7.39953V8.12C7.39953 9.75 8.24753 11.184 9.52753 12C8.24753 12.816 7.39953 14.25 7.39953 15.88V18.56H5.67953C5.59153 18.56 5.51953 18.632 5.51953 18.72V19.84C5.51953 19.928 5.59153 20 5.67953 20H18.3195C18.4075 20 18.4795 19.928 18.4795 19.84V18.72C18.4795 18.632 18.4075 18.56 18.3195 18.56H16.5995V15.88C16.5995 14.25 15.7515 12.816 14.4715 12C15.7515 11.184 16.5995 9.75 16.5995 8.12ZM15.1595 15.88V18.56H8.83953V15.88C8.83953 15.036 9.16753 14.242 9.76553 13.646C10.3615 13.048 11.1555 12.72 11.9995 12.72C12.8435 12.72 13.6375 13.048 14.2335 13.646C14.8315 14.242 15.1595 15.036 15.1595 15.88ZM15.1595 8.12C15.1595 8.964 14.8315 9.758 14.2335 10.354C13.6375 10.952 12.8435 11.28 11.9995 11.28C11.1555 11.28 10.3615 10.952 9.76553 10.354C9.47085 10.0616 9.23723 9.71347 9.07827 9.32996C8.9193 8.94644 8.83815 8.53515 8.83953 8.12V5.44H15.1595V8.12Z'
            fill='#6347FF'
        />
    </svg>
);

const clockSvg = (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
            d='M16.8 12.8C16.8 13.24 16.44 13.6 16 13.6H12.8C11.92 13.6 11.2 12.88 11.2 12V8C11.2 7.56 11.56 7.2 12 7.2C12.44 7.2 12.8 7.56 12.8 8V11.2C12.8 11.64 13.16 12 13.6 12H16C16.44 12 16.8 12.36 16.8 12.8ZM20 12C20 16.416 16.416 20 12 20C7.584 20 4 16.416 4 12C4 7.584 7.584 4 12 4C16.416 4 20 7.584 20 12ZM18.4 12C18.4 8.472 15.528 5.6 12 5.6C8.472 5.6 5.6 8.472 5.6 12C5.6 15.528 8.472 18.4 12 18.4C15.528 18.4 18.4 15.528 18.4 12Z'
            fill='#6347FF'
        />
    </svg>
);

const snackbarProps = {
    anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
    ClickAwayListenerProps: {
        onClickAway: () => {},
    },
};

function SlideTransition(props) {
    return <Slide {...props} direction={'left'} />;
}

const StripeContext = createContext({});
const useStripe = () => useContext(StripeContext);

const StripeProvider = ({ children }) => {
    const { customer, subscription, setSubscription, setEntitlements } = useApp();
    const { auth, currentUser } = useAuth();
    const history = useHistory();
    const location = useLocation();
    const { message } = useSnackbar();
    const { device } = useViewport();
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(true);
    const [welcomeDialog, setWelcomeDialog] = useState(false);
    const [paywallDialog, setPaywallDialog] = useState(false);
    const [paymentDialog, setPaymentDialog] = useState({ open: false, isLoading: false, stepIndex: 0, isAnnual: true });
    const [updateCardDialog, setUpdateCardDialog] = useState({ open: false, isLoading: false, reactivate: false });
    const [toast, setToast] = useState({
        open: false,
        content: null,
    });

    const openToast = (content) => {
        setToast((current) => ({ ...current, open: true, content }));
    };

    const closeToast = () => {
        setToast((current) => ({ ...current, open: false }));
    };

    const hitPaywall = () => {
        setPaywallDialog(true);
    };

    const initUpgradeFlow = useCallback(() => {
        closeToast();
        setWelcomeDialog(false);
        setPaywallDialog(false);
        setPaymentDialog({ open: true, isLoading: false, stepIndex: 0, isAnnual: true });
    }, []);

    const goToNextStep = () => {
        setPaymentDialog((current) => ({ ...current, stepIndex: current.stepIndex + 1 }));
    };

    const goToPrevStep = () => {
        setPaymentDialog((current) => ({ ...current, stepIndex: current.stepIndex - 1 }));
    };

    const openTrialReminderToast = useCallback(() => {
        if (
            !currentUser?.profile_set_up ||
            !currentUser?.finished_onboarding ||
            !subscription ||
            isLoading ||
            paymentDialog.open
        ) {
            closeToast();
            return;
        }

        if (subscription.items.data[0].price.id === TG_FREE_PLAN_PRICE_ID) {
            openToast(
                <Stack alignItems={'flex-start'} spacing={3}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        {clockSvg}
                        <Text variant={'p1'} weight={'semibold'}>
                            {t('stripe.free-trial-ended', 'Your free trial has ended')}
                        </Text>
                    </Stack>
                    <Text>
                        <Trans
                            i18nKey='stripe.free-trial-ended-description'
                            components={{
                                semibold: <Text component={'span'} weight={'semibold'} />,
                            }}
                        >
                            Your free trial of TimelyGrader's <semibold>Starter Plan</semibold> has ended, and you've
                            been moved to the <semibold>Free Plan</semibold>. To continue enjoying paid features, you
                            can upgrade anytime.
                        </Trans>
                    </Text>
                    <Button variant={'borderless'} onClick={initUpgradeFlow}>
                        {t('controls.upgrade', 'Upgrade')}
                    </Button>
                </Stack>
            );
        } else if (subscription.status === 'trialing') {
            const now = new Date().getTime();
            const expiry = new Date(subscription.trial_end * 1000).getTime();
            const daysUntilExpiry = moment(expiry).diff(moment(now), 'days');
            openToast(
                <Stack alignItems={'flex-start'} spacing={3}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        {hourGlassSvg}
                        <Text variant={'p1'} weight={'semibold'}>
                            {t('stripe.free-trial-countdown', {
                                count: daysUntilExpiry + 1,
                            })}
                        </Text>
                    </Stack>
                    <Text>
                        <Trans
                            i18nKey='stripe.upgrade-before-date'
                            components={{
                                date: (
                                    <Text component={'span'} weight={'semibold'}>
                                        {moment(expiry).format('MMM Do, YYYY')}
                                    </Text>
                                ),
                            }}
                        >
                            Enjoying TimelyGrader's paid features? Upgrade before <date /> to ensure there will be no
                            interruption to your account.
                        </Trans>
                    </Text>
                    <Button variant={'borderless'} onClick={initUpgradeFlow}>
                        {t('controls.upgrade', 'Upgrade')}
                    </Button>
                </Stack>
            );
        }
    }, [
        currentUser?.finished_onboarding,
        currentUser?.profile_set_up,
        initUpgradeFlow,
        isLoading,
        paymentDialog.open,
        subscription,
        t,
    ]);

    const cancelAtPeriodEnd = async () => {
        const updatedSubscription = await stripeService.updateSubscription(customer.id, subscription.id, {
            action: 'cancel_at_period_end',
        });
        setSubscription(updatedSubscription);
        message.success(
            t('message.starter-plan-cancelled', 'Your Starter Plan subscription was cancelled successfully.')
        );
    };

    const undoCancelAtPeriodEnd = async () => {
        const updatedSubscription = await stripeService.updateSubscription(customer.id, subscription.id, {
            action: 'undo_cancel_at_period_end',
        });
        setSubscription(updatedSubscription);
    };

    const detachPaymentMethod = async () => {
        await stripeService.detachDefaultPaymentMethod(customer.id);
    };

    const upgradeSubscription = async (isAnnual) => {
        const updatedSubscription = await stripeService.updateSubscription(customer.id, subscription.id, {
            action: 'upgrade_using_existing_payment_method',
            is_annual: isAnnual,
        });
        setSubscription(updatedSubscription);
    };

    const scheduleSubscriptionBillingPeriodChange = async () => {
        setSubscription();
        const { subscription: updatedSubscription } = await stripeService.updateSubscription(
            customer.id,
            subscription.id,
            {
                action: 'schedule_subscription_billing_period_change',
            }
        );
        setSubscription(updatedSubscription);
        message.success(
            t(
                'message.plan-switch-info',
                'Your plan will switch to {{interval}} billing at the end of your current billing period.',
                {
                    interval: subscription.plan.interval === 'year' ? 'monthly' : 'annual',
                }
            )
        );
    };

    const undoScheduleSubscriptionBillingPeriodChange = async () => {
        setSubscription();
        const updatedSubscription = await stripeService.updateSubscription(customer.id, subscription.id, {
            action: 'undo_schedule_subscription_billing_period_change',
        });
        setSubscription(updatedSubscription);
        message.success(
            t(
                'message.plan-switch-undo-info',
                'Your plan has been updated and will continue with {{interval}} billing.',
                {
                    interval: subscription.plan.interval === 'year' ? 'annual' : 'monthly',
                }
            )
        );
    };

    useEffect(() => {
        if (!auth) return closeToast();
        openTrialReminderToast();
    }, [auth, message, openTrialReminderToast]);

    useEffect(() => {
        if (!auth?.user_id || !customer?.id || !subscription?.id) {
            return setIsLoading(false);
        } else {
            setIsLoading(true);
        }

        const params = qs.parse(location.search);
        if (
            Object.keys(params).includes('redirect_status') &&
            Object.keys(params).includes('setup_intent') &&
            Object.keys(params).includes('setup_intent_client_secret')
        ) {
            (async () => {
                const { action, subscription: updatedSubscription } = await stripeService.updateSubscription(
                    customer.id,
                    subscription.id,
                    {
                        action: 'handle_setup_intent',
                        setup_intent: params.setup_intent,
                    }
                );
                if (action === 'update_subscription') {
                    setPaymentDialog((current) => ({
                        ...current,
                        open: true,
                        stepIndex: 2,
                    }));
                    setSubscription(updatedSubscription);
                    const entitlements = await userService.getUserEntitlements(auth.user_id, customer.id);
                    setEntitlements(entitlements);
                }
                if (action === 'update_payment_method') {
                    message.success(
                        t('message.payment-details-updated', 'Your payment details were updated successfully.')
                    );
                    history.replace({ search: '' });
                }
                if (action === 'update_payment_method_and_undo_cancel_at_period_end') {
                    setSubscription(updatedSubscription);
                    message.success(
                        t('message.starter-plan-reactivated', 'Your Starter Plan was reactivated successfully.')
                    );
                    history.replace({ search: '' });
                }
                setIsLoading(false);
            })();
        } else {
            setIsLoading(false);
        }
    }, [
        auth?.user_id,
        customer?.id,
        history,
        location,
        message,
        setEntitlements,
        setSubscription,
        subscription?.id,
        t,
    ]);

    return (
        <StripeContext.Provider
            value={{
                welcomeDialog,
                setWelcomeDialog,
                paywallDialog,
                setPaywallDialog,
                paymentDialog,
                setPaymentDialog,
                updateCardDialog,
                setUpdateCardDialog,
                hitPaywall,
                initUpgradeFlow,
                goToNextStep,
                goToPrevStep,
                openTrialReminderToast,
                cancelAtPeriodEnd,
                undoCancelAtPeriodEnd,
                detachPaymentMethod,
                upgradeSubscription,
                scheduleSubscriptionBillingPeriodChange,
                undoScheduleSubscriptionBillingPeriodChange,
            }}
        >
            {isLoading ? <Loading /> : children}
            <WelcomeDialog />
            <PaywallDialog />
            <PaymentDialog />
            <UpdateCardDialog />
            <Snackbar
                key={'stripe-snackbar'}
                sx={{ left: 'unset', maxWidth: device === 'mobile' ? '308px' : 'unset' }}
                anchorOrigin={snackbarProps.anchorOrigin}
                ClickAwayListenerProps={snackbarProps.ClickAwayListenerProps}
                TransitionComponent={SlideTransition}
                open={toast.open}
                onClose={closeToast}
            >
                <Alert severity={'system'} onClose={closeToast}>
                    {toast.content}
                </Alert>
            </Snackbar>
        </StripeContext.Provider>
    );
};

export { useStripe };
export default StripeProvider;
